import {
  configurationsGetRfpConfigurationOptions,
  useHeyApi,
} from "@platform/api";
import { CoverageTypeEnum } from "@platform/types";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

/**
 * Coverage type → the MP configuration sheet holding that coverage's field
 * settings. The sheet keys are mpx's validation catalog
 * (`internal/validation/catalog.go`), which mirrors the Chameleon workbook tabs.
 *
 * The workbook also carries variant sheets (Dental Dual Option, Vol LTD, …) that
 * the side-by-side has no separate coverage for; those are left out until the
 * grid models them.
 */
const SHEET_BY_COVERAGE: Partial<Record<string, string>> = {
  // One Dental configuration drives every dental sheet, DHMO included — unless
  // the coverage is quoted as multiple options, which resolves to
  // `dental_multiple_options` below (CD-1970).
  [CoverageTypeEnum.DENTAL_PPO]: "dental",
  [CoverageTypeEnum.DENTAL_HMO]: "dental",
  [CoverageTypeEnum.VISION]: "vision",
  [CoverageTypeEnum.STD]: "std",
  [CoverageTypeEnum.LTD]: "ltd",
  [CoverageTypeEnum.BASIC_LIFE_ADND]: "basic_life",
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: "vol_life",
  [CoverageTypeEnum.ACCIDENT]: "accident",
  [CoverageTypeEnum.EAP]: "eap",
  [CoverageTypeEnum.CRITICAL_ILLNESS]: "critical_illness",
  [CoverageTypeEnum.CANCER]: "cancer",
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: "hospital_indemnity",
  [CoverageTypeEnum.WHOLE_LIFE]: "whole_life",
  [CoverageTypeEnum.FMLA]: "fmla",
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: "idi",
};

/**
 * The MP configuration field keys shown for a coverage on this RFP (CD-386).
 *
 * The stored polarity is the inverse of the Chameleon workbook's: the workbook
 * marks a row with `x` to hide it, and the portal inverted that on import
 * because the negative form read as confusing — so `true` means show. A hidden
 * field is stored `false` or, for the fields a broker's column marked `x`, left
 * out of the sheet map altogether. Absence therefore means hidden, which is why
 * this returns the shown set rather than a hidden one.
 *
 * `undefined` means no configuration applies at all — nothing resolved for the
 * RFP, or a coverage the workbook has no sheet for. Callers render every row in
 * that case, so an unconfigured RFP never comes up empty.
 *
 * A configuration that *did* resolve but carries no fields for the coverage is a
 * different thing: it shows nothing there, so the set comes back empty and every
 * keyed row is hidden. Absence inside a resolved configuration means hidden, the
 * same as it does field by field.
 */
export const useVisibleMpConfigKeys = (
  ecoRfpId: string | undefined,
  coverageType: string | undefined,
  /**
   * True when the coverage is quoted as multiple options — the presentation
   * renders it on the Dual or Tri Option sheet, which carries its own settings.
   * Only dental has a separate set today.
   */
  multipleOptions = false,
): ReadonlySet<string> | undefined => {
  const { centroPortalClient } = useHeyApi();

  const { data } = useQuery({
    ...configurationsGetRfpConfigurationOptions({
      path: { rfpId: ecoRfpId as string },
      client: centroPortalClient,
    }),
    enabled: !!ecoRfpId,
  });

  const configuration = data?.configuration;
  const mapped = coverageType ? SHEET_BY_COVERAGE[coverageType] : undefined;
  const sheet =
    multipleOptions && mapped === "dental" ? "dental_multiple_options" : mapped;

  // Memoized on the query data, which React Query keeps referentially stable
  // while the payload is unchanged: the Quotes grid rebuilds every row when
  // this set changes identity.
  return useMemo(() => {
    if (!configuration || !sheet) {
      return undefined;
    }

    const fields = configuration.configuration?.quotes?.[sheet] ?? {};

    return new Set(
      Object.entries(fields)
        .filter(([, isShown]) => isShown)
        .map(([field]) => field),
    );
  }, [configuration, sheet]);
};

/**
 * The grid field keys of a coverage's extended (non-v5) fields (CD-2301).
 *
 * Backed by the same RFP configuration fetch as {@link useVisibleMpConfigKeys},
 * but reads its static `extendedFieldKeys` catalog rather than a per-RFP
 * toggle: which fields are extended is a structural fact about the LOC, not
 * something a broker's configuration turns on or off, so the result does not
 * vary by RFP or by `source`. Unlike row visibility, editability never
 * distinguishes the multiple-options dental sheet from the plain one — both
 * dental coverage types resolve to the same "dental" catalog entry.
 *
 * Empty for a coverage with no extended fields, and for an unknown coverage
 * type.
 */
export const useExtendedFieldKeys = (
  ecoRfpId: string | undefined,
  coverageType: string | undefined,
): ReadonlySet<string> => {
  const { centroPortalClient } = useHeyApi();

  const { data } = useQuery({
    ...configurationsGetRfpConfigurationOptions({
      path: { rfpId: ecoRfpId as string },
      client: centroPortalClient,
    }),
    enabled: !!ecoRfpId,
  });

  const sheet = coverageType ? SHEET_BY_COVERAGE[coverageType] : undefined;
  const extendedFieldKeys = sheet
    ? data?.extendedFieldKeys?.[sheet]
    : undefined;

  // Memoized for the same reason as useVisibleMpConfigKeys: the Quotes grid
  // rebuilds every column when this set changes identity.
  return useMemo(
    () => (sheet ? new Set(extendedFieldKeys ?? []) : NO_EXTENDED_FIELD_KEYS),
    [sheet, extendedFieldKeys],
  );
};

const NO_EXTENDED_FIELD_KEYS: ReadonlySet<string> = new Set();

export default useVisibleMpConfigKeys;
